import { __decorate } from "tslib";
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject, distinctUntilChanged, map, of } from 'rxjs';
import { SailsSocketService } from '../../modules/sails-socket';
import { LprWorkflow } from 'src/ng-app/features/lpr-management';
import { isEmpty, get } from 'lodash';
import { ResourceListService } from 'src/ng-app/shared/services/resource-list.service';
let LPRManagementService = class LPRManagementService {
    sails = inject(SailsSocketService);
    resourceListService = inject(ResourceListService);
    lprResourceTypeId = window.appConfig.lprResourceTypeId;
    lprResourceType = null;
    workflows = new Map();
    editingWorkflows = new Map();
    _selectedOrg$ = new BehaviorSubject(null);
    selectedOrg$ = this._selectedOrg$.pipe(distinctUntilChanged());
    _selectedWorkflow$ = new BehaviorSubject(null);
    selectedWorkflow$ = this._selectedWorkflow$.pipe(distinctUntilChanged());
    workflowUpdated$ = new Subject();
    setSelectedOrg(org) {
        this._selectedOrg$.next(org);
    }
    setSelectedWorkflow(workflow) {
        this._selectedWorkflow$.next(workflow);
    }
    informWorkflowUpdated(workflow) {
        this.workflowUpdated$.next(workflow);
    }
    getLprResourceType() {
        if (this.lprResourceType) {
            return of(this.lprResourceType);
        }
        return this.sails.get(`/type/${this.lprResourceTypeId}`)
            .pipe(map((response) => {
            this.lprResourceType = response.data;
            return this.lprResourceType;
        }));
    }
    getLprWorkflows(org) {
        const url = `/eventrule${org ? `?org=${org}` : ''}`;
        return this.sails.get(url)
            .pipe(map(response => this.initAndStoreWorkflows(response.data)));
    }
    getLprListsForOrg(org) {
        return this.resourceListService.getResourceListForOrg(org)
            .pipe(map((lists) => {
            const lprLists = lists.filter((list) => {
                const resourceType = list.type;
                return get(resourceType, 'id', resourceType) === this.lprResourceTypeId;
            });
            return lprLists;
        }));
    }
    isEditableWorkflow(workflow) {
        return workflow instanceof LprWorkflow && workflow.isEditMode();
    }
    cloneForEdit(workflow) {
        const edit = new LprWorkflow(workflow, true);
        this.editingWorkflows.set(workflow.id, edit);
        return edit;
    }
    getWorkflowById(id) {
        return this.workflows.get(id);
    }
    saveWorkflow(workflow) {
        return new Observable((subscriber) => {
            let subscription;
            const changes = workflow.getChanges();
            if (isEmpty(changes)) {
                const workflowId = workflow.id || 'unknown ID';
                subscriber.error(new Error(`No changes detected for workflow with ID: ${workflowId}.`));
                subscriber.complete();
            }
            else {
                const createOrUpdateObs = workflow.isNew() ?
                    this.sails.post('/eventRule', changes) :
                    this.sails.put(`/eventRule/${workflow.id}`, changes);
                subscription = createOrUpdateObs
                    .subscribe({
                    next: ({ data: model }) => {
                        let found = this.workflows.get(model.id);
                        if (!found) {
                            const newWorkflow = new LprWorkflow(model);
                            this.workflows.set(newWorkflow.id, newWorkflow);
                            found = newWorkflow;
                            workflow.updateModel(newWorkflow);
                        }
                        found.updateModel(model);
                        workflow.resetChanges();
                        subscriber.next(workflow);
                        subscriber.complete();
                        this.informWorkflowUpdated(found);
                    },
                    error: (err) => {
                        subscriber.error(err);
                        subscriber.complete();
                    }
                });
            }
            return {
                unsubscribe() {
                    if (subscription) {
                        subscription.unsubscribe();
                    }
                }
            };
        });
    }
    initAndStoreWorkflows(workflows) {
        workflows.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
        workflows.forEach((workflow) => {
            this.workflows.set(workflow.id, new LprWorkflow(workflow));
        });
        return [...this.workflows.values()];
    }
};
LPRManagementService = __decorate([
    Injectable({
        providedIn: 'root',
    })
], LPRManagementService);
export { LPRManagementService };
